import { createBrowserRouter, RouterProvider, Navigate } from 'react-router-dom';
import { Suspense, lazy } from 'react';
// Import layouts and components
import AuthLayout from '../Layouts/AuthLayout';
import ProtectedRoute from './ProtectedRoute';
import PrivateLayout from '../Layouts/PrivateLayout';
import ErrorBoundary from '../components/ErrorBoundary';
import NotFoundPage from '../Pages/NotFoundPage';
// Import pages
import Dashboard from '../Pages/Dashboard';
import TeamManagement from '../Pages/teams/TeamUsers';
import ChargersList from '../Pages/chargers/ChargersPage';
import ChargingHistoryTable from '../Pages/charging-history/ChargingHistoryTable';
import DiagnosticPage from '../Pages/diagnostic/diagnostic';
import ForgetPassword from '../Pages/ForgetPassword';
import UnauthorizedPage from '../Pages/UnauthorizedPage';
import LocationsPage from '../Pages/locations/LocationsPage';
import AuditLogs from '../Pages/audit/AuditLogs';
import RFIDTable from '../Pages/rfid-manager/RFIDPage';
import CustomersListPage from '../Pages/customers/CustomersListPage';
import ReportPage from '../Pages/report/ReportPage';
import EmbeddedPage from '../Pages/OCPP-logs/Page';
import LocationOwnerList from '../Pages/LocationsOwnerList/page';
import SettingsPage from '../Pages/settings/SettingsPage';
import NotificationsPage from '../Pages/notifications/NotificationsPage';
import CronListPage from '../Pages/cron-list';
import SapLogsPage from '../Pages/sap-logs';
import TermsAndConditions from '../Pages/TermsAndConditions';
import PrivacyPolicy from '../Pages/PrivacyPolicy';
import RFIDTransactionList from '../Pages/rfid-manager/RFIDTransactionList'; // Import new component
import EvVehicleModelsPage from '../Pages/ev-vehicle-models/EvVehicleModelsPage';
import FactoriesPage from '../Pages/factories/FactoriesPage';
import DesignationsPage from '../Pages/designations/DesignationsPage';
import TouPricingPage from '../Pages/tou-pricing/TouPricingPage';
// Lazy load LoginPage
const LoginPage = lazy(() => import('../Pages/Login'));

// Define application routes
const router = createBrowserRouter([
  {
    path: '/', // Public routes
    element: <AuthLayout />,
    children: [
      {
        index: true, // Default route for the root path
        element: <LoginPage />,
      },
      {
        path: '/forgot-password', // Forgot password page
        element: <ForgetPassword />,
      },
      {
        path: '/terms-and-conditions', // Public terms and conditions page
        element: <TermsAndConditions />,
      },
      {
        path: '/privacy-policy', // Public privacy policy page
        element: <PrivacyPolicy />,
      },
    ],
  },
  {
    path: '/dashboard/unauthorized', // Outside ADMIN gate so blocked users can see it
    element: (
      <ProtectedRoute>
        <UnauthorizedPage />
      </ProtectedRoute>
    ),
  },
  {
    path: '/dashboard', // Protected routes under dashboard
    element: (
      <ProtectedRoute
        allowedRoles={['ADMIN']}
      >
        <PrivateLayout />
      </ProtectedRoute>
    ),
    children: [
      {
        index: true, // Default dashboard page
        element: <Dashboard />,
      },
      {
        path: '/dashboard/operators', // Hidden: single-tenant — redirect away
        element: <Navigate to="/dashboard" replace />,
      },
      {
        path: '/dashboard/rfid', // RFID management
        element: (
          <ProtectedRoute
            allowedRoles={['ADMIN']}
          >
            <RFIDTable />
          </ProtectedRoute>
        ),
      },
      {
        path: '/dashboard/rfid-transactions', // RFID transactions
        element: (
          <ProtectedRoute
            allowedRoles={['ADMIN']}
          >
            <RFIDTransactionList />
          </ProtectedRoute>
        ),
      },
      {
        path: '/dashboard/ev-vehicle-models',
        element: (
          <ProtectedRoute
            allowedRoles={['ADMIN']}
          >
            <EvVehicleModelsPage />
          </ProtectedRoute>
        ),
      },
      {
        path: '/dashboard/audit', // Audit logs
        element: (
          <ProtectedRoute
            allowedRoles={['ADMIN']}
          >
            <AuditLogs />
          </ProtectedRoute>
        ),
      },
      {
        path: '/dashboard/locations', // Locations management
        element: (
          <ProtectedRoute
            allowedRoles={['ADMIN']}
          >
            <LocationsPage />
          </ProtectedRoute>
        ),
      },
      {
        path: '/dashboard/Teams', // Team management
        element: (
          <ProtectedRoute
            allowedRoles={['ADMIN']}
          >
            <TeamManagement />
          </ProtectedRoute>
        ),
      },
      {
        path: '/dashboard/reports', // Reports page
        element: (
          <ProtectedRoute
            allowedRoles={['ADMIN']}
          >
            <ReportPage />
          </ProtectedRoute>
        ),
      },
      {
        path: '/dashboard/tou-pricing',
        element: (
          <ProtectedRoute
            allowedRoles={['ADMIN']}
          >
            <TouPricingPage />
          </ProtectedRoute>
        ),
      },
      {
        path: '/dashboard/customers', // Employee list
        element: (
          <ProtectedRoute
            allowedRoles={['ADMIN']}
          >
            <CustomersListPage />
          </ProtectedRoute>
        ),
      },
      {
        path: '/dashboard/factories',
        element: (
          <ProtectedRoute
            allowedRoles={['ADMIN']}
          >
            <FactoriesPage />
          </ProtectedRoute>
        ),
      },
      {
        path: '/dashboard/positions',
        element: (
          <ProtectedRoute
            allowedRoles={['ADMIN']}
          >
            <DesignationsPage />
          </ProtectedRoute>
        ),
      },
      {
        path: '/dashboard/chargers', // Chargers list
        element: (
          <ProtectedRoute
            allowedRoles={['ADMIN']}
          >
            <ChargersList />
          </ProtectedRoute>
        ),
      },
      {
        path: '/dashboard/charging-history', // Charging history
        element: (
          <ProtectedRoute
            allowedRoles={['ADMIN']}
          >
            <ChargingHistoryTable />
          </ProtectedRoute>
        ),
      },
      {
        path: '/dashboard/OCPP-logs', // OCPP logs
        element: (
          <ProtectedRoute
            allowedRoles={['ADMIN']}
          >
            <EmbeddedPage />
          </ProtectedRoute>
        ),
      },
      {
        path: '/dashboard/locationsOwners', // Locations list
        element: (
          <ProtectedRoute allowedRoles={['ADMIN']}>
            <LocationOwnerList />
          </ProtectedRoute>
        ),
      },
      {
        path: '/dashboard/diagnostic', // Diagnostic page
        element: (
          <ProtectedRoute
            allowedRoles={['ADMIN']}
          >
            <DiagnosticPage />
          </ProtectedRoute>
        ),
      },
      {
        path: '/dashboard/settings', // Settings page
        element: (
          <ProtectedRoute
            allowedRoles={['ADMIN']}
          >
            <SettingsPage />
          </ProtectedRoute>
        ),
      },
      {
        path: '/dashboard/notifications',
        element: (
          <ProtectedRoute allowedRoles={['ADMIN']}>
            <NotificationsPage />
          </ProtectedRoute>
        ),
      },
      {
        path: '/dashboard/cron-list', // Cron list
        element: (
          <ProtectedRoute allowedRoles={['ADMIN']}>
            <CronListPage />
          </ProtectedRoute>
        ),
      },
      {
        path: '/dashboard/sap-logs',
        element: (
          <ProtectedRoute allowedRoles={['ADMIN']}>
            <SapLogsPage />
          </ProtectedRoute>
        ),
      },
    ],
  },
  {
    path: '*', // Catch-all route for 404 errors
    element: <NotFoundPage />,
  },
]);

// AppRoutes component to provide routing
const AppRoutes = () => {

  return (
    <ErrorBoundary>
      <Suspense fallback={<div className="flex justify-center items-center h-screen"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div></div>}>
        <RouterProvider router={router} />
      </Suspense>
    </ErrorBoundary>
  );
};

export default AppRoutes;
